import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_CONFIG } from "../config/recaptcha";
import "../styles/InviteModal.css";

const InviteModal = ({ isOpen, onClose, doctorName, doctorEmail }) => {
  const [patientName, setPatientName] = useState("");
  const [patientEmail, setPatientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  const handleInvite = async (e) => {
    e.preventDefault();
    
    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      setStatus("Please complete the reCAPTCHA verification.");
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    
    try {
      const response = await fetch("https://directory.virtualtriage.ai/doctors/invite/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          doctor_email: doctorEmail,
          patient_email: patientEmail,
          patient_name: patientName,
          message,
          recaptcha_token: captchaValue, // Send reCAPTCHA token to backend
        }),
      });
      
      const data = await response.json();
      
      if (data.status) {
        setStatus("Invitation sent successfully!");
        setPatientName("");
        setPatientEmail("");
        setMessage("");
        setCaptchaValue(null);
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        // Close modal after successful invitation
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setStatus(data.message || "Failed to send invitation.");
        // Reset reCAPTCHA on failure
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaValue(null);
      }
    } catch (error) {
      setStatus("Error sending invitation. Please try again.");
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaValue(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus(null);
    setPatientName("");
    setPatientEmail("");
    setMessage("");
    setCaptchaValue(null);
    setIsSubmitting(false);
    // Reset reCAPTCHA
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    onClose();
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    // Clear any previous error messages when user completes reCAPTCHA
    if (value && status && status.includes("reCAPTCHA")) {
      setStatus(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Invite Practitioner</h2>
        <p>
          This practitioner{doctorName ? ` (${doctorName})` : ""} has not joined Virtual Triage yet. You can easily invite them by filling out your information and submitting the form. We will notify them that you have invited them to join Virtual Triage.
        </p>
        <form onSubmit={handleInvite}>
          <input
            type="text"
            placeholder="Your Full Name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            required
            disabled={isSubmitting}
          />
          <input
            type="email"
            placeholder="Your Email"
            value={patientEmail}
            onChange={(e) => setPatientEmail(e.target.value)}
            required
            disabled={isSubmitting}
          />
          <textarea
            placeholder="Invitation Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            disabled={isSubmitting}
          />
          
          {/* reCAPTCHA */}
          <div className="recaptcha-container">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_CONFIG.SITE_KEY}
              onChange={handleCaptchaChange}
              theme="light"
              size="normal"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={isSubmitting || !captchaValue}
            className={isSubmitting || !captchaValue ? "submit-btn-disabled" : ""}
          >
            {isSubmitting ? "Sending..." : "Send Invite"}
          </button>
        </form>
        
        {status && (
          <div className={`status-message ${status.includes("success") ? "success" : "error"}`}>
            {status}
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteModal;
